import { Box, Card, IconButton, Tooltip, tooltipClasses, Typography, useMediaQuery } from '@mui/material'
import { ContractInfo, Dropdown, FilterButton, InvoiceStatus, TableApiV2, TableHeaderApi } from '~/components'
import { useContract } from '~/contexts'
import { InvoiceListDrawer } from './components'
import { SearchQueysData, useQueryString, useToggle } from '~/hooks'
import { useFilters, useRowSelect, useSortBy, useTable, usePagination, Column } from 'react-table'
import { useMemo, useState } from 'react'
import { EInvoiceStatus, InvoiceListQuery, PaymentModel, useInvoiceListQuery } from '~/graphql/types'
import { IconDots, IconDownload, IconQuestionMark, IconRefresh, IconX } from '@tabler/icons-react'
import { addHours, formatDate, formatMoney, monthNameByMonthNumber, transformMoney, Unarray } from '~/utils'
import { useTheme } from '@emotion/react'
import { StyledA } from '~/pages/InvoicesList/styles'
import { InvoiceCancelDialog, SecondInvoiceGenerateModal } from '~/pages/InvoicesList/components'

export const Invoices: React.FC = () => {
  const theme = useTheme()
  const { contract } = useContract()
  const { isTrue: isVisibleInvoiceCancelModal, toggle: toggleInvoiceCancelModal, setIsTrue: setIsVisibleInvoiceCancelModal } = useToggle()
  const { isTrue: invoiceModalIsOpen, setIsTrue: setModalInvoiceIsOpen } = useToggle(false)
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string>('')
  const isLowerLg = useMediaQuery(theme.breakpoints.down('lg'))
  const isLowerMd = useMediaQuery(theme.breakpoints.down('md'))
  const isLowerSm = useMediaQuery(theme.breakpoints.down('sm'))

  const queryKeys: SearchQueysData[] = [
    { key: 'status', defaultValue: [], parseValue: true },
    { key: 'by', defaultValue: [], parseValue: true },
    { key: 'begin', defaultValue: '' },
    { key: 'end', defaultValue: '' },
    { key: 'dueBegin', defaultValue: '' },
    { key: 'dueEnd', defaultValue: '' },
    { key: 'paymentTypes', defaultValue: [], parseValue: true },
  ]

  const { searchQuerys, paginationQuerySize, searchParams, setSearchParams, page, size } = useQueryString()

  const queryValues = searchQuerys(queryKeys)

  const { isTrue: drawerIsOpen, toggle: toggleDrawer, setIsTrue: setDrawerIsOpen } = useToggle()
  // const { isTrue: secondInvoiceModalIsVisible, setIsTrue: setSecondInvoiceModalIsVisible } = useToggle()
  // const { isTrue: shareInvoiceIsVisible, setIsTrue: setShareInvoiceIsVisible } = useToggle(true)

  // const cards: InvoiceCardProps[] = [
  //   {
  //     title: 'saldo de geração',
  //     description: 'KWh disponíveis para o cliente',
  //     value: '576 Kwh',
  //     icon: <IconBolt size={24} color={theme.palette.despertaPurple.main} />
  //   },
  //   {
  //     title: 'total de benefício',
  //     description: 'Soma do benefício para o cliente',
  //     value: 'R$ 1.222,63',
  //     icon: <IconPigMoney size={24} color={theme.palette.despertaPurple.main} />
  //   },
  //   {
  //     title: 'valor da desperta',
  //     description: 'Valor líquido espero para a empresa',
  //     value: 'R$ 3.020,11',
  //     icon: <IconSunHigh size={24} color={theme.palette.despertaPurple.main} />
  //   },
  //   {
  //     title: 'valor em aberto',
  //     description: 'Soma de faturas não pagas (3)',
  //     value: 'R$ 3.775,14',
  //     icon: <IconReceipt2 size={24} color={theme.palette.despertaPurple.main} />
  //   },
  // ]

  const { data, loading, refetch } = useInvoiceListQuery({
    variables: {
      params: {
        pageSize: size,
        pageNumber: page,
        contractId: contract?._id,
        sort: {
          field: 'createdAt',
          order: -1
        },
        ...queryValues.status?.length > 0 ? { inStatus: queryValues.status } : {},
        ...(queryValues.status?.includes('paid') && queryValues.paymentTypes?.length > 0 ? { paymentTypes: queryValues.paymentTypes } : {}),
        ...queryValues.by?.length > 0 ? { secondCopy: queryValues.by.map((item: string) => item === 'true' ? true : false) } : {},
        ...queryValues?.begin && queryValues?.end ?
          {
            createdAt:
            {
              startDate: new Date(queryValues.begin),
              endDate: new Date(`${queryValues.end}Z23:59:59`)
            }
          }
          : {},
        ...queryValues?.dueBegin && queryValues?.dueEnd ?
          {
            dueDate:
            {
              startDate: new Date(queryValues.dueBegin),
              endDate: new Date(`${queryValues.dueEnd}Z23:59:59`)
            }
          }
          : {},
      }
    },
    onCompleted(result) {
      setSearchParams(state => {
        state.set('items', String(result.invoiceList.meta.documentCounts))
        return state
      })
    },
    fetchPolicy: 'no-cache'
  })

  const selectedInvoice: Unarray<InvoiceListQuery['invoiceList']['data']> | null = data?.invoiceList.data.find(item => item._id === selectedInvoiceId) || null

  const columns = useMemo((): Column[] => {
    return [

      {
        Header: 'Referência',
        accessor: 'energyBill',
        Cell: (props) => <>{monthNameByMonthNumber(props.value.reference.month)}, {Number(props.value.reference.year) + 2000}</>
      },
      {
        Header: 'Data de criação',
        accessor: 'createdAt',
        Cell: (props) => <>{formatDate(props.value)}</>
      },
      {
        Header: 'Vencimento',
        accessor: 'dueDate',
        Cell: (props) => {

          const newDate = addHours(new Date(props.value), 3)
          return <>{formatDate(newDate)}</>

        }
      },
      {
        Header: 'Pagamento',
        accessor: 'payment',
        Cell: (props) => {
          const itemPaidAt = props.value.find((item: PaymentModel) => item.paidAt !== null)?.paidAt
          return (
            <> {itemPaidAt ? formatDate(itemPaidAt) : '-'}</>
          )
        }
      },
      {
        Header: 'Valor',
        accessor: 'amountCents',
        disableSortBy: true,
        Cell: (props) => <>{props.value ? formatMoney(transformMoney(props.value, 'toReal')) : '-'}</>
      },
      {
        Header: 'Status',
        accessor: 'status',
        disableSortBy: true,
        Cell: (props) => <InvoiceStatus status={props.value} />
      },
      {
        Header: 'Ações',
        accessor: '_id',
        Cell: ({ value, row }: any) => {
          const isCanceled = row.original.status === EInvoiceStatus.canceled
          const isLastRow = row.index === size - 1
          const isEnableToCancelInvoice = row.original.status !== EInvoiceStatus.canceled && row.original.status !== EInvoiceStatus.paid && row.original.status !== EInvoiceStatus.generatePDF && row.original.status !== EInvoiceStatus.requested && !(row.original.status === EInvoiceStatus.externalNegotiation)
          const isExpiredInvoice = row.original.status === EInvoiceStatus.expired
          return (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              {isCanceled ? (
                <Tooltip title='Conferir motivo de cancelamento' placement='bottom-start'>
                  <IconButton onClick={() => {
                    setSelectedInvoiceId(value)
                    toggleInvoiceCancelModal()
                  }} sx={{ '&:hover': { backgroundColor: '#3BA1FF20' } }}>
                    <IconQuestionMark />
                  </IconButton>
                </Tooltip>
              ) : (
                <Dropdown $minWidht='200px' $options={{ left: '-160px', top: isLastRow ? '-130px' : 'unset' }} $usePortal={size <= 4} elementClick={
                  <Tooltip
                    title='Ver lista de ações com a fatura'
                    placement='bottom-start'

                    slotProps={{
                      popper: {
                        sx: {
                          [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]: {
                            marginTop: '3px',
                          },
                        },
                      },
                    }}
                  >
                    <IconButton><IconDots /></IconButton>
                  </Tooltip>
                }
                >
                  <Card sx={{ width: '100%', paddingY: '1rem', display: 'flex', flexFlow: 'column', gap: '.4rem', zIndex: '999' }}>
                    <StyledA href={`${import.meta.env.VITE_APP_REST_API_URL}customer-area/download/invoice/${value}`} target='_blank' rel='noreferrer'>
                      <Box sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', width: '100%', gap: '6px', padding: '.6rem 1rem', transition: 'all .2s ease-in-out', '&:hover': { background: '#00000010' } }}>
                        <IconDownload color='#000' />
                        <Typography variant='body2' color='black.light'>
                          Baixar fatura
                        </Typography>
                      </Box>
                    </StyledA>
                    {isExpiredInvoice &&
                      (
                        <Tooltip title='Gerar 2ª via da fatura' placement='bottom'>
                          <Box
                            sx={{
                              cursor: isEnableToCancelInvoice ? 'pointer' : 'not-allowed',
                              display: 'flex',
                              alignItems: 'center',
                              width: '100%',
                              gap: '6px',
                              padding: '.6rem 1rem',
                              transition: 'all .2s ease-in-out',
                              '&:hover': {
                                background: isEnableToCancelInvoice ? '#00000010' : 'none',
                              },
                              opacity: isEnableToCancelInvoice ? 1 : 0.5,
                            }}
                            onClick={() => (setSelectedInvoiceId(value), setModalInvoiceIsOpen(true))}>

                            <IconRefresh />

                            <Typography variant='body2' color='black.light'>
                              Gerar 2ª via da fatura
                            </Typography>
                          </Box>

                        </Tooltip>
                      )
                    }
                    <Tooltip
                      title={isEnableToCancelInvoice ? '' : 'Essa fatura não pode ser cancelada'}
                      placement='bottom'
                    >
                      <Box
                        onClick={() => {
                          if (isEnableToCancelInvoice) {
                            setSelectedInvoiceId(value)
                            toggleInvoiceCancelModal()
                          }
                        }}
                        sx={{
                          cursor: isEnableToCancelInvoice ? 'pointer' : 'not-allowed',
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                          gap: '6px',
                          padding: '.6rem 1rem',
                          transition: 'all .2s ease-in-out',
                          '&:hover': {
                            background: isEnableToCancelInvoice ? '#00000010' : 'none',
                          },
                          opacity: isEnableToCancelInvoice ? 1 : 0.5,
                        }}
                      >
                        <IconX color={isEnableToCancelInvoice ? '#EF4444' : '#B0B0B0'} />
                        <Typography variant='body2' color='black.light'>
                          Cancelar fatura
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Card>
                </Dropdown>
              )}

            </Box >
          )
        }

      },
      // {
      //   Header: 'Ações',
      //   accessor: '_id',
      //   disableSortBy: true,
      //   Cell: (props) => <Link to={`${import.meta.env.VITE_APP_REST_API_URL}customer-area/download/${props.value}`}><IconButton><IconDots /></IconButton></Link>
      // },
    ]
  }, [])

  const dataWithMemo = useMemo(() => data?.invoiceList.data || [], [data])

  const defaultColumnHiddens = isLowerLg ?
    [''].concat(isLowerMd ? [''] : '').concat(isLowerSm ? [''] : '')
    : []

  const tableMethods = useTable({
    columns,
    data: dataWithMemo,
    defaultColumn: {
      Filter: <></>
    },
    manualPagination: true,
    initialState: {
      hiddenColumns: defaultColumnHiddens
    }
  },
    useFilters,
    useSortBy,
    usePagination,
    useRowSelect
  )

  const reloadInvoiceList = () => {
    refetch()
  }

  return (
    <>
      <Box sx={{ width: '100%' }}>
        <ContractInfo contract={contract} />
        {/* <Typography sx={{ fontSize: '1.5rem', fontWeight: 500, color: theme.palette.grey[800], paddingBottom: '.6rem' }}>Visão geral de faturas</Typography> */}
        {/* <GridColumn
          responsive={[{ breakWidth: theme.breakpoints.values.lg, columns: 2 }, { breakWidth: theme.breakpoints.values.sm, columns: 1 }]}
          columns={4}
          >
          {cards.map((card) => {
            return (
              <InvoiceCard {...card} key={card.title} />
              )
              })}
              </GridColumn> */}

        <TableHeaderApi
          startElement={(
            <Typography
              sx={{
                fontSize: '1.5rem',
                fontWeight: 500,
                color: theme.palette.grey[800],
              }}>
              Histórico de faturas
            </Typography>
          )}
          endElement={(
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <FilterButton
                  filtersToClear={queryKeys}
                  toggleDrawer={toggleDrawer}
                  filterCounter={searchParams.size - paginationQuerySize}
                />
              </Box>

              <InvoiceListDrawer
                setDrawerIsOpen={setDrawerIsOpen}
                queryKeys={queryKeys}
                drawerIsOpen={drawerIsOpen}
                toggleDrawer={toggleDrawer}
              />
            </>
          )} />

        <TableApiV2
          isLoading={loading}
          tableMethods={tableMethods}
          columns={columns}
        />
      </Box>
      {/* <SecondInvoiceModal isVisible={secondInvoiceModalIsVisible} setIsVisible={setSecondInvoiceModalIsVisible} />
      <ShareInvoiceModal typeShare='whatsapp' isVisible={shareInvoiceIsVisible} setIsVisible={setShareInvoiceIsVisible} /> */}
      <InvoiceCancelDialog
        isVisible={isVisibleInvoiceCancelModal}
        setIsVisibleInvoiceCancelModal={setIsVisibleInvoiceCancelModal}
        invoiceId={selectedInvoiceId}
        onInvoiceCancelSuccess={reloadInvoiceList}
        selectedInvoice={selectedInvoice} />
      {invoiceModalIsOpen && Boolean(selectedInvoiceId) && (
        <SecondInvoiceGenerateModal
          invoiceId={selectedInvoiceId}
          contractId={selectedInvoice?.contract?.id || null}
          isVisible={invoiceModalIsOpen}
          onClose={() => setModalInvoiceIsOpen(false)}
          onSuccess={reloadInvoiceList}
        />
      )}
    </>
  )
}
